import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';

// Real country-level facts — not operational mock data
const NIGERIA_STATS = {
  population: 225_082_083,
  registeredVoters: 93_469_008,
  electionsTracked: 21,
  averageTurnout: 46.09,
  nextElectionYear: 2027,
};

function useCountUp(target: number, duration = 1800, active = false) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    let raf: number;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);

  return value;
}

function formatStat(n: number, decimals = 0): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return decimals > 0 ? n.toFixed(decimals) : String(n);
}

const NigerianFlag: React.FC = () => (
  <Box
    aria-label="Nigerian flag"
    sx={{
      display: 'flex',
      width: 44,
      height: 30,
      borderRadius: 0.75,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      flexShrink: 0,
    }}
  >
    <Box sx={{ flex: 1, bgcolor: '#008751' }} />
    <Box sx={{ flex: 1, bgcolor: '#ffffff' }} />
    <Box sx={{ flex: 1, bgcolor: '#008751' }} />
  </Box>
);

interface StatCounterProps {
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  icon?: React.ReactNode;
  accent?: boolean;
  active: boolean;
}

const StatCounter: React.FC<StatCounterProps> = ({
  label, value, suffix = '', prefix = '', decimals = 0, icon, accent = false, active,
}) => {
  const theme = useTheme();
  const count = useCountUp(value, 1800, active);

  const displayValue =
    decimals > 0
      ? count.toFixed(decimals)
      : formatStat(count);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: 2.5,
        px: 2,
        borderRadius: 2,
        bgcolor: accent
          ? alpha(theme.palette.secondary.main, 0.15)
          : alpha('#ffffff', 0.06),
        border: '1px solid',
        borderColor: accent
          ? alpha(theme.palette.secondary.main, 0.4)
          : alpha('#ffffff', 0.12),
        backdropFilter: 'blur(4px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          bgcolor: accent
            ? alpha(theme.palette.secondary.main, 0.22)
            : alpha('#ffffff', 0.1),
          transform: 'translateY(-2px)',
        },
      }}
    >
      {icon && (
        <Box sx={{ color: accent ? theme.palette.secondary.light : '#79BD79', mb: 0.75, opacity: 0.9 }}>
          {icon}
        </Box>
      )}
      <Typography
        component="div"
        sx={{
          fontSize: { xs: '1.6rem', sm: '2rem' },
          fontWeight: 800,
          lineHeight: 1,
          color: accent ? theme.palette.secondary.light : '#ffffff',
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          mb: 0.5,
        }}
      >
        {prefix}{displayValue}{suffix}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: alpha('#ffffff', 0.6),
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: '0.65rem',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const NigeriaHero: React.FC = () => {
  const theme = useTheme();
  const heroRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={heroRef}
      sx={{
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        mb: 4,
        background: `linear-gradient(135deg, #042e04 0%, #084608 55%, #0d5a0d 100%)`,
        boxShadow: '0 8px 32px rgba(4,46,4,0.35)',
      }}
    >
      {/* Decorative background ring */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha('#79BD79', 0.12)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -60,
          left: -60,
          width: 240,
          height: 240,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, sm: 4 } }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
          <NigerianFlag />
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: alpha('#ffffff', 0.55),
                letterSpacing: '0.14em',
                fontSize: '0.65rem',
                lineHeight: 1,
                display: 'block',
              }}
            >
              Federal Republic of
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '1.6rem', sm: '2.25rem' },
                fontWeight: 800,
                color: '#ffffff',
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
              }}
            >
              Nigeria
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: alpha('#ffffff', 0.55),
            mb: 3.5,
            fontSize: '0.85rem',
            letterSpacing: '0.02em',
          }}
        >
          Electoral intelligence platform · West Africa's largest democracy
        </Typography>

        {/* Stat counters */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <StatCounter
            label="Population"
            value={NIGERIA_STATS.population}
            active={active}
          />
          <StatCounter
            label="Registered Voters"
            value={NIGERIA_STATS.registeredVoters}
            active={active}
          />
          <StatCounter
            label="Avg. Turnout"
            value={NIGERIA_STATS.averageTurnout}
            suffix="%"
            decimals={1}
            active={active}
          />
          <StatCounter
            label="Elections Tracked"
            value={NIGERIA_STATS.electionsTracked}
            active={active}
          />
          <StatCounter
            label="Next General Election"
            value={NIGERIA_STATS.nextElectionYear}
            accent
            active={active}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default NigeriaHero;
